import { useQuery } from "@tanstack/react-query"
import { SectionHero, StatGrid, MetricCard, AdminCard, StatusPill } from "@/components/admin/primitives"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Skeleton } from "@/components/ui/skeleton"
import { supabase } from "@/integrations/supabase/client"
import { Video, Clock, CheckCircle2, XCircle, AlertTriangle, RefreshCw, Play, Pause } from "lucide-react"
import { Link } from "react-router-dom"
import { cn } from "@/lib/utils"

interface PipelineRun {
  id: string
  user_id: string
  status: string
  analysis_stage: string
  analysis_progress: number
  created_at: string
  updated_at: string
  original_filename: string
  pose_data: boolean
  analysis_data: boolean
  analysis_enriched: boolean
  coaching_result: boolean
  ai_result: boolean
  profiles?: {
    full_name?: string
    first_name?: string
    last_name?: string
  }
}

export default function PipelineMonitor() {
  // Recent runs query
  const { data: recentRuns, isLoading: runsLoading, refetch: refetchRuns } = useQuery({
    queryKey: ["admin_pipeline_runs"],
    queryFn: async () => {
      // Note: We can't easily check if JSONB columns are null without fetching them
      // For now, we rely on status to indicate completion state
      const { data, error } = await supabase
        .from('swing_runs')
        .select(`
          id,
          user_id,
          status,
          analysis_stage,
          analysis_progress,
          created_at,
          updated_at,
          original_filename,
          profiles:user_id (
            full_name,
            first_name,
            last_name
          )
        `)
        .order('created_at', { ascending: false })
        .limit(50)
      
      if (error) throw error
      
      // Without fetching large JSONB columns, we infer status from the run state
      return data.map(run => ({
        ...run,
        // Use status to infer which columns are populated
        pose_data: run.status === 'ready' || run.status === 'processing',
        analysis_data: run.status === 'ready',
        analysis_enriched: run.status === 'ready',
        coaching_result: run.status === 'ready',
        ai_result: run.status === 'ready',
      })) as PipelineRun[]
    },
    refetchInterval: 10000, // Refresh every 10 seconds
  })

  // Pipeline stats
  const { data: stats, isLoading: statsLoading } = useQuery({
    queryKey: ["admin_pipeline_stats"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('swing_runs')
        .select('status, analysis_stage, created_at')
        .gte('created_at', new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString())
      
      if (error) throw error
      
      const total = data.length
      const completed = data.filter(r => r.status === 'ready').length
      const failed = data.filter(r => r.status === 'failed').length
      const processing = data.filter(r => r.status === 'processing').length
      const uploading = data.filter(r => r.status === 'uploading').length
      
      // Stage breakdown
      const stages = data.reduce((acc, run) => {
        acc[run.analysis_stage] = (acc[run.analysis_stage] || 0) + 1
        return acc
      }, {} as Record<string, number>)
      
      return {
        total,
        completed,
        failed,
        processing,
        uploading,
        successRate: total > 0 ? ((completed / total) * 100) : 0,
        stages,
      }
    },
    refetchInterval: 30000,
  })

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'ready': return <CheckCircle2 className="h-4 w-4 text-admin-success" />
      case 'failed': return <XCircle className="h-4 w-4 text-admin-danger" />
      case 'processing': return <RefreshCw className="h-4 w-4 text-admin-info animate-spin" />
      case 'uploading': return <Play className="h-4 w-4 text-admin-warning" />
      default: return <Pause className="h-4 w-4 text-muted-foreground" />
    }
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'ready': return 'success'
      case 'failed': return 'danger'
      case 'processing': return 'info'
      case 'uploading': return 'warning'
      default: return 'muted'
    }
  }

  const getPipelineProgress = (run: PipelineRun) => {
    const steps = [
      { key: 'pose_data', label: 'Pose' },
      { key: 'analysis_data', label: 'Analysis' },
      { key: 'analysis_enriched', label: 'Enriched' },
      { key: 'coaching_result', label: 'Coaching' },
    ]
    
    const completed = steps.filter(step => run[step.key as keyof PipelineRun]).length
    return { completed, total: steps.length, steps }
  }

  return (
    <div className="min-h-screen bg-surface pb-20">
      <div className="max-w-admin mx-auto px-admin-content-padding py-8">
        {/* Hero Section */}
        <SectionHero
          title="Video Pipeline Monitor"
          description="Real-time monitoring of video analysis and AI coaching pipeline"
          gradient="primary"
          accessory={
            <div className="flex items-center gap-2">
              <StatusPill 
                status={stats && stats.successRate >= 95 ? "healthy" : stats && stats.successRate >= 85 ? "degraded" : "critical"}
                label={`${stats?.successRate.toFixed(1) ?? 0}% Success Rate`}
              />
              <Badge variant="outline" className="bg-primary/10 text-primary border-primary/20">
                <Video className="h-3 w-3 mr-1" />
                {stats?.total ?? 0} Videos (24h)
              </Badge>
            </div>
          }
          actions={
            <Button onClick={() => refetchRuns()}>
              <RefreshCw className="h-4 w-4 mr-2" />
              Refresh
            </Button>
          }
        />

        {/* Pipeline Stats */}
        <div className="mt-8">
          <h2 className="text-admin-section font-semibold text-foreground mb-4">Pipeline Statistics (24h)</h2>
          {statsLoading ? (
            <StatGrid columns={5}>
              {[1, 2, 3, 4, 5].map((i) => (
                <Skeleton key={i} className="h-32" />
              ))}
            </StatGrid>
          ) : (
            <StatGrid columns={5}>
              <MetricCard
                title="Total Runs"
                value={stats?.total.toLocaleString() ?? "0"}
                size="large"
              />
              <MetricCard
                title="Completed"
                value={stats?.completed ?? 0}
                description={`${stats?.successRate.toFixed(1) ?? 0}% success rate`}
                size="large"
                accentColor="success"
              />
              <MetricCard
                title="Processing"
                value={stats?.processing ?? 0}
                description="Currently running"
                size="large"
                accentColor="info"
              />
              <MetricCard
                title="Failed"
                value={stats?.failed ?? 0}
                description="Requires attention"
                size="large"
                accentColor={stats && stats.failed > 0 ? "danger" : "success"}
              />
              <MetricCard
                title="Uploading"
                value={stats?.uploading ?? 0}
                description="In upload queue"
                size="large"
                accentColor="warning"
              />
            </StatGrid>
          )}
        </div>

        {/* Recent Runs */}
        <div className="mt-8">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-admin-section font-semibold text-foreground">Recent Pipeline Runs</h2>
            <Link to="/admin/debug">
              <Button variant="outline" size="sm">
                <AlertTriangle className="h-4 w-4 mr-2" />
                Debug Tools
              </Button>
            </Link>
          </div>
          
          {runsLoading ? (
            <div className="space-y-3">
              {[1, 2, 3, 4, 5].map((i) => (
                <Skeleton key={i} className="h-20" />
              ))}
            </div>
          ) : (
            <div className="space-y-3">
              {recentRuns?.map((run) => {
                const progress = getPipelineProgress(run)
                return (
                  <AdminCard key={run.id} className="p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className="flex items-center gap-2">
                          {getStatusIcon(run.status)}
                          <div>
                            <p className="text-sm font-medium text-foreground">
                              {run.original_filename || `Run ${run.id.slice(0, 8)}`}
                            </p>
                            <p className="text-xs text-muted-foreground">
                              {run.profiles?.full_name || run.profiles?.first_name || 'Unknown User'} • {new Date(run.created_at).toLocaleString()}
                            </p>
                          </div>
                        </div>
                        
                        <div className="flex items-center gap-2">
                          <StatusPill 
                            status={getStatusColor(run.status)} 
                            label={run.status}
                          />
                          <Badge variant="outline" className="text-xs">
                            {run.analysis_stage}
                          </Badge>
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-4">
                        {/* Pipeline Progress */}
                        <div className="flex items-center gap-1">
                          {progress.steps.map((step, index) => (
                            <div
                              key={step.key}
                              className={cn(
                                "w-3 h-3 rounded-full border-2",
                                run[step.key as keyof PipelineRun]
                                  ? "bg-admin-success border-admin-success"
                                  : "bg-muted border-muted-foreground/20"
                              )}
                              title={step.label}
                            />
                          ))}
                        </div>
                        
                        <div className="text-right">
                          <p className="text-xs text-muted-foreground">
                            {progress.completed}/{progress.total} steps
                          </p>
                          <p className="text-xs text-muted-foreground">
                            {run.analysis_progress}% complete
                          </p>
                        </div>
                      </div>
                    </div>
                  </AdminCard>
                )
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
